import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import MobileHeader from "./MobileHeader";
import MbCoTrader from "./MbCoTrader";
import CoTraderStocksMobile from "./CoTraderStocksMobile";
import CoTraderCustomTickerTape from "../TradingView/CoTraderTickerTape";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import CoTraderCombinedMobileFiltersBar from "./CoTraderCombinedMobileFiltersBar";
import {
  DEFAULT_FX_TRADE_TYPE,
  EMPTY_TICKER_UNIVERSE,
  FX_ASSET_TYPE,
  fetchTickerUniverse,
  findSelectionForQuoteSymbol,
  flattenAssetOptions,
  getFxRuntimeSymbol,
  resolveTickerUniverseSelection,
} from "./TickerUniverseOptions";
import {
  COTRADER_LANGUAGE_LIST,
  getStoredCoTraderLanguage,
} from "./Utilities/languages";

const COMBINED_SELECTION_STORAGE_KEY = "cotrader_combined_selection";

const getStoredSelection = () => {
  try {
    return JSON.parse(localStorage.getItem(COMBINED_SELECTION_STORAGE_KEY) || "null");
  } catch (error) {
    console.warn("Failed to load combined CoTrader selection", error);
    return null;
  }
};

const CoTraderCombinedMobile = () => {
  const { isDark } = useTheme();
  const [tickerUniverse, setTickerUniverse] = useState(EMPTY_TICKER_UNIVERSE);
  const [isUniverseLoading, setIsUniverseLoading] = useState(true);
  const [universeError, setUniverseError] = useState("");
  const [selection, setSelection] = useState(
    () =>
      getStoredSelection() || {
        assetType: "",
        country: null,
        category: null,
        tradeType: DEFAULT_FX_TRADE_TYPE,
        value: "",
      },
  );
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [language, setLanguage] = useState(() => getStoredCoTraderLanguage());
  const [languageKey, setLanguageKey] = useState(0);

  const styles = useMemo(
    () =>
      isDark
        ? {
            pageBg: "#0B0F1A",
            surface: "#12172A",
            border: "#1E2642",
            muted: "#A6B0CF",
          }
        : {
            pageBg: "#FFFFFF",
            surface: "#FFFFFF",
            border: "#E9ECEF",
            muted: "#5B6472",
          },
    [isDark],
  );

  useEffect(() => {
    let isMounted = true;

    const loadTickerUniverse = async () => {
      setIsUniverseLoading(true);
      setUniverseError("");

      try {
        const nextTickerUniverse = await fetchTickerUniverse();
        if (!isMounted) return;
        setTickerUniverse(nextTickerUniverse);
      } catch (error) {
        if (error?.status === 401) {
          window.location.href = "/logout";
          return;
        }

        if (!isMounted) return;
        setTickerUniverse(EMPTY_TICKER_UNIVERSE);
        setUniverseError(error?.message || "Ticker universe is unavailable.");
      } finally {
        if (isMounted) {
          setIsUniverseLoading(false);
        }
      }
    };

    void loadTickerUniverse();

    return () => {
      isMounted = false;
    };
  }, []);

  const resolvedSelection = useMemo(
    () =>
      resolveTickerUniverseSelection({
        options: tickerUniverse.options,
        defaultSelection: tickerUniverse.defaultSelection,
        selection,
      }),
    [selection, tickerUniverse.defaultSelection, tickerUniverse.options],
  );
  const isFxAsset = resolvedSelection.assetType === FX_ASSET_TYPE;
  const selectedLabel = resolvedSelection.option?.label || resolvedSelection.value || "CoTrader";
  const scopedAssetOptions = useMemo(
    () => flattenAssetOptions(tickerUniverse.options, resolvedSelection.assetType),
    [resolvedSelection.assetType, tickerUniverse.options],
  );
  const tickerTapeSymbols = useMemo(() => {
    const nextSymbols = [
      resolvedSelection.option?.quote_symbol || "",
      ...scopedAssetOptions.map((option) => option.quote_symbol),
    ].filter(Boolean);

    return Array.from(new Set(nextSymbols));
  }, [resolvedSelection.option?.quote_symbol, scopedAssetOptions]);
  const tickerTapeLabels = useMemo(
    () =>
      scopedAssetOptions.reduce((labels, option) => {
        if (option.quote_symbol) {
          labels[option.quote_symbol] = option.label || option.quote_symbol;
        }
        return labels;
      }, {}),
    [scopedAssetOptions],
  );
  const scopedStockOptions = useMemo(() => {
    if (isFxAsset || !resolvedSelection.assetType) {
      return {};
    }

    return tickerUniverse.options[resolvedSelection.assetType] || {};
  }, [isFxAsset, resolvedSelection.assetType, tickerUniverse.options]);

  useEffect(() => {
    localStorage.setItem(
      COMBINED_SELECTION_STORAGE_KEY,
      JSON.stringify({
        assetType: resolvedSelection.assetType,
        country: resolvedSelection.country,
        category: resolvedSelection.category,
        tradeType: resolvedSelection.tradeType,
        value: resolvedSelection.value,
      }),
    );

    if (isFxAsset) {
      localStorage.setItem("default_symbol", getFxRuntimeSymbol(resolvedSelection.value));
      localStorage.setItem(
        "cotrader_combined_trade_type",
        resolvedSelection.tradeType || DEFAULT_FX_TRADE_TYPE,
      );
      return;
    }

    if (resolvedSelection.country) {
      localStorage.setItem("default_country_stock", resolvedSelection.country);
    }
    if (resolvedSelection.category) {
      localStorage.setItem("default_category_stock", resolvedSelection.category);
    }
    if (resolvedSelection.value) {
      localStorage.setItem("default_symbol_stock", resolvedSelection.value);
    }
  }, [isFxAsset, resolvedSelection]);

  useEffect(() => {
    if (localStorage.getItem("user_language") !== language) {
      localStorage.setItem("user_language", language);
    }
  }, [language]);

  const handleTickerTapeSelection = (quoteSymbol) => {
    const nextSelection = findSelectionForQuoteSymbol(
      tickerUniverse.options,
      resolvedSelection.assetType,
      quoteSymbol,
    );

    if (!nextSelection) {
      return;
    }

    setSelection((previousSelection) => ({
      ...previousSelection,
      ...nextSelection,
    }));
  };

  const renderEmbeddedProduct = () => {
    if (!resolvedSelection.assetType || !resolvedSelection.option) {
      return (
        <div className="px-3 pb-3">
          <div
            className="rounded-4 border px-3 py-3"
            style={{
              background: styles.surface,
              borderColor: styles.border,
              color: styles.muted,
              fontWeight: 600,
            }}
          >
            {universeError || "No eligible CoTrader instruments are available for this user."}
          </div>
        </div>
      );
    }

    if (isFxAsset) {
      return (
        <MbCoTrader
          hideTopChrome
          instrumentTypeOverride={resolvedSelection.tradeType}
          languageOverride={language}
          selectedSymbolOverride={getFxRuntimeSymbol(resolvedSelection.value)}
          showSuggestionsOverride={showSuggestions}
        />
      );
    }

    return (
      <CoTraderStocksMobile
        defaultSymbolOverride={resolvedSelection.value}
        hideTopChrome
        languageOverride={language}
        selectedSymbolOverride={resolvedSelection.value}
        selectedTickerLabelOverride={resolvedSelection.option.label || ""}
        showSuggestionsOverride={showSuggestions}
        stockOptionsOverride={scopedStockOptions}
        stockUniverseErrorOverride={universeError}
        stockUniverseLoadingOverride={isUniverseLoading}
      />
    );
  };

  return (
    <>
      <Helmet>
        <title>CoTrader | EMAlpha</title>
      </Helmet>

      <div
        className="w-100 h-100 d-flex flex-column"
        style={{ height: "100dvh", background: styles.pageBg }}
        key={languageKey}
      >
        <MobileHeader />

        <div className="px-2 pt-2">
          {isUniverseLoading ? (
            <div className="px-3 py-2" style={{ color: styles.muted, fontWeight: 600 }}>
              Loading ticker universe...
            </div>
          ) : universeError || tickerTapeSymbols.length === 0 ? (
            <div className="px-3 py-2" style={{ color: "#D9534F", fontWeight: 600 }}>
              {universeError || "Ticker tape unavailable."}
            </div>
          ) : (
            <CoTraderCustomTickerTape
              symbols={tickerTapeSymbols}
              symbolLabels={tickerTapeLabels}
              dark={isDark}
              onSymbolClick={handleTickerTapeSelection}
            />
          )}
        </div>

        <CoTraderCombinedMobileFiltersBar
          universeOptions={tickerUniverse.options}
          selection={resolvedSelection}
          onSelectionChange={setSelection}
          isDark={isDark}
          isDisabled={isUniverseLoading || Boolean(universeError)}
          selectedLabel={selectedLabel}
          showSuggestions={showSuggestions}
          setShowSuggestions={setShowSuggestions}
          language={language}
          setLanguage={setLanguage}
          languageList={COTRADER_LANGUAGE_LIST}
          setLanguageKey={setLanguageKey}
        />

        <div className="flex-grow-1 overflow-hidden">{renderEmbeddedProduct()}</div>
      </div>
    </>
  );
};

export default CoTraderCombinedMobile;
